import {TrackKey} from 'models/configuration/TrackModel'
import {RegistrationModel} from 'models/registration/RegistrationModel'
import {StepConfig, StepKey, StepModel} from 'models/registration/StepModel'
import {WysiwygType} from 'types'
import {Memoize} from 'util/memoize'

export type DetailsStepConfig = StepConfig & {
	personal_text: WysiwygType
	company_text: WysiwygType
	invoice_text: WysiwygType

	already_registered_title: string
	already_registered_text: WysiwygType
	free_title: string
	free_text: WysiwygType
	free_with_paid_options_text: string
	free_imec_text: string
}

export type RegistrationStatus =
	| {
			type: 'invitee'
			free_options: boolean
			free_tracks: Array<TrackKey>
			imec: boolean
	  }
	| {type: 'regular'}
	| {type: 'registered'}

export type DetailsStepState = DetailsInfoState & {
	status?: RegistrationStatus
}

export type DetailsInfoState = {
	// Personal
	firstname?: string
	lastname?: string
	email?: string

	not_affiliated?: boolean

	// Company
	function?: string
	company?: string
	company_belgian?: boolean
	kp?: string
	industry?: string
	industry_description?: string
	street?: string
	number?: string
	box?: string
	postal_code?: string
	city?: string
	state?: string
	country?: string
	phone?: string
	website?: string
	vat?: string
	vat_not_applicable?: boolean

	// Invoice address
	other_invoice_address?: boolean
	invoice_address?: string
	invoice_address_line2?: string
	invoice_postal_code?: string
	invoice_city?: string
	invoice_state?: string
	invoice_country?: string
	invoice_vat?: string
}

const requiredFields: Array<keyof DetailsStepState> = [
	'firstname',
	'lastname',
	'email',
	'country'
]

export class DetailsStepModel extends StepModel<DetailsStepConfig> {
	@Memoize()
	get key(): StepKey {
		return 'details'
	}

	@Memoize()
	get state(): DetailsStepState {
		return this.registration.state.details
	}

	@Memoize()
	get email(): string {
		return this.state.email?.toLowerCase()
	}

	@Memoize()
	get company(): string {
		return this.state.company?.toLowerCase()
	}

	@Memoize()
	get worksForBelgianCompany(): boolean {
		return !!this.state.company_belgian
	}

	@Memoize()
	get hasVatFilled(): boolean {
		return !!this.state.vat && !this.shouldShowKPInputField
	}

	@Memoize()
	get shouldDescribeIndustry(): boolean {
		return this.state.industry === 'Other'
	}

	@Memoize()
	get isApplicable(): boolean {
		return true
	}

	@Memoize()
	get shouldShowTours(): boolean {
		// Checkbox "I work for a Flemish Company, the Flemish government, ..." is niet aangevinkt -> geen tours
		if (!this.worksForBelgianCompany) return false

		// Company of email is nog niet ingevuld -> geen TOURS
		if (!this.company || !this.email) return false

		// Company naam bevat 'imec' -> geen tours
		if (this.company.includes('imec')) return false

		// Emailadres eindigt op @imec.be, maar niet op ext@imec.be -> geen tours
		if (this.email.endsWith('@imec.be') && !this.email.endsWith('ext@imec.be'))
			return false

		// Voldoet aan alle criteria -> toon tours
		return true
	}

	@Memoize()
	get shouldShowAffiliatedFields(): boolean {
		return (
			(!this.event.state.no_show && !this.registration.isImecInvitee) ||
			(!this.registration.isInvitee && this.event.state.no_show)
		)
	}

	@Memoize()
	get shouldShowCompanyFields(): boolean {
		return (
			!this.state.not_affiliated ||
			(this.registration.isInvitee && this.event.state.no_show)
		)
	}

	@Memoize()
	get shouldShowKPInputField(): boolean {
		if (this.registration.config.no_kp) return false

		if (this.registration.isImecInvitee) return true

		if (this.registration.isInviteOnly) return false

		// Company of email is nog niet ingevuld -> geen kp veld
		if (!this.company || !this.email) return false

		// Company naam bevat GEEN 'imec' -> geen kp veld
		if (!this.company.includes('imec')) return false

		// Emailadres eindigt NIET op @imec.be -> geen kp veld
		if (!this.email.endsWith('@imec.be')) return false

		// Emailadres eindigt op ext@imec.be -> geen kp veld
		if (this.email.endsWith('ext@imec.be')) return false

		// Voldoet aan alle criteria -> toon kp veld
		return true
	}

	@Memoize()
	get shouldShowVATFields(): boolean {
		return (
			this.shouldShowCompanyFields &&
			!this.shouldShowKPInputField &&
			!this.registration.isFreeEvent
		)
	}

	@Memoize()
	get shouldShowAddressFields(): boolean {
		return (
			(this.shouldShowCompanyFields && this.hasVatFilled) ||
			(this.event.state.no_show &&
				!this.state.not_affiliated &&
				!this.shouldShowKPInputField)
		)
	}

	@Memoize()
	get shouldShowInvoiceAddressFields(): boolean {
		return (
			(this.shouldShowCompanyFields &&
				this.hasVatFilled &&
				!!this.registration.totalPrice) ||
			(this.shouldShowVATFields && this.event.state.no_show)
		)
	}

	@Memoize()
	get texts() {
		return {
			personal: this.config.personal_text,
			company: this.config.company_text,
			invoice: this.config.invoice_text
		}
	}

	@Memoize()
	get invalidFieldKey(): string {
		return requiredFields.find(key => !this.state[key])
	}

	@Memoize()
	get isReady(): boolean {
		return !this.invalidFieldKey
	}

	getIsRequiredField(key: keyof DetailsInfoState): boolean {
		if (new Set(['function', 'company', 'industry']).has(key))
			return this.shouldShowCompanyFields

		if (new Set(['vat']).has(key))
			return this.shouldShowCompanyFields && this.event.state.no_show

		if (new Set(['street', 'number', 'postal_code', 'city']).has(key))
			return this.shouldShowAddressFields

		if (
			new Set([
				'invoice_address',
				'invoice_postal_code',
				'invoice_city',
				'invoice_country',
				'invoice_vat'
			]).has(key)
		)
			return this.state.other_invoice_address

		if (key === 'kp') return this.shouldShowKPInputField
		return !!requiredFields.find(k => k === key)
	}

	validate(values: DetailsInfoState): Record<string, string> {
		const obj: Record<string, string> = {}

		if (
			this.shouldShowKPInputField &&
			values.kp &&
			!this.validateKp(values.kp)
		) {
			obj.kp = 'KP number should be 5 digits or NONE.'
		}

		if (
			this.shouldShowCompanyFields &&
			values.phone &&
			!this.validatePhone(values.phone)
		) {
			obj.phone =
				'Phone number should include only numbers, optionally preceded by a plus sign.'
		}

		if (
			this.registration.isInviteOnly &&
			this.registration.isConfirmedNotInvitee
		) {
			obj.email =
				'This event is only for invitees. Check if you filled in the email address for which you received an invite or contact our support.'
		}
		if (this.registration.isAlreadyRegistered) {
			obj.email = 'This email address is already registered.'
		}

		return obj
	}

	validateKp(number: string): boolean {
		if (number === 'NONE') return true
		if (number.length !== 5) return false
		const isNumber = /^\d+$/.test(number)
		return isNumber
	}

	validatePhone(number: string): boolean {
		const isNumber = /^\+?\d+$/.test(number)
		return isNumber
	}

	// ----- State changes ----- //
	validateEmail(email, status: RegistrationStatus): RegistrationModel {
		if (this.email !== email) return this.registration

		// If user has a free invitation, check if he/she has alternative packs
		if (
			status.type === 'invitee' &&
			status.free_tracks &&
			status.free_tracks.length > 0
		) {
			const chosenTracks = [...this.registration.tracksSet]
			// Remove tracks for which the user has a free invitation
			const remainingTracks = chosenTracks.filter(
				track => !status.free_tracks.includes(track.key)
			)
			// Find an alternative pack for each of the tracks
			if (remainingTracks.length > 0) {
				const alternativePacks = chosenTracks.map(track => {
					return track.alternativePack()
				})
				const newRegistration = {
					...this.registration.state,
					details: {...this.registration.state.details, status}
				}
				const currentPrice = new RegistrationModel(
					this.registration.config,
					this.registration.event,
					newRegistration
				).totalPrice
				const newPrice = new RegistrationModel(
					this.registration.config,
					this.registration.event,
					{...newRegistration, packs: alternativePacks}
				).totalPrice
				if (
					chosenTracks.length === alternativePacks.length &&
					newPrice < currentPrice
				) {
					return this.registration.update({
						details: {...this.registration.state.details, status},
						packs: alternativePacks,
						validated: true
					})
				}
			}
		}

		return this.registration.update({
			details: {...this.registration.state.details, status},
			validated: true
		})
	}

	invalidateEmail() {
		return this.registration.update({
			validated: false
		})
	}

	setDetails(fields: Partial<DetailsInfoState>): RegistrationModel {
		const status = this.state.email !== fields.email ? null : this.state.status

		return this.registration.update({
			details: {
				...fields,
				status,
				kp: this.shouldShowKPInputField ? fields.kp : undefined
			},
			type: this.registration.isInvitee ? 'invitee' : null,
			tags: {
				...this.registration.state.tags,
				Invitee: this.registration.isInvitee
			}
		})
	}
}
